import { computed, reactive, ref, watch } from "vue";
import type { ComputedRef, Ref } from "vue";
import { ElMessage } from "element-plus";
import { logger } from "@/utils/logger";
import { useNotificationDisplayPolicy } from "@/composables/useNotificationDisplayPolicy";

export type NotificationLevel = "info" | "warning" | "error" | "critical";

export interface NotificationAction {
  label: string;
  url: string;
}

export interface NotificationScope {
  fund_id: string | null;
  account_id: string | null;
}

export interface RawNotificationMessage {
  table: "notification";
  delivery?: "history" | "realtime" | string;
  id?: string | number;
  source?: string | number | null;
  level?: NotificationLevel | string | null;
  category?: string | number | null;
  title?: string | number | null;
  body?: string | number | null;
  scope?: Partial<NotificationScope> | null;
  dedup_key?: string | number | null;
  ts?: number | string | null;
  ttl?: number | string | null;
  actions?: NotificationAction[] | null;
  state?: "firing" | "resolved" | string | null;
  /** 保留发送方维度；展示准入仅使用后端 category 策略。 */
  domain?: string | null;
}

export interface RawLegacyAlertMessage {
  table: "alert";
  type?: Exclude<NotificationLevel, "critical"> | string | null;
  title?: string | number | null;
  msg?: string | number | null;
}

export type RawNotification =
  | RawNotificationMessage
  | RawLegacyAlertMessage
  | Record<string, unknown>;

export interface NotificationGroup {
  key: string;
  state?: "firing" | "resolved";
  level: NotificationLevel;
  category: string;
  source: string;
  title: string;
  body: string;
  count: number;
  latestTs: number;
  scope: NotificationScope;
  actions: NotificationAction[];
  raw: RawNotification[];
  ttl?: number;
  expired: boolean;
  read: boolean;
}

export interface NotificationFilters {
  source?: string;
  level?: string;
  fundId?: string;
  accountId?: string;
}

export interface UseNotificationBusReturn {
  items: ComputedRef<NotificationGroup[]>;
  visibleItems: ComputedRef<NotificationGroup[]>;
  filteredItems: ComputedRef<NotificationGroup[]>;
  unreadCount: ComputedRef<number>;
  highestLevel: ComputedRef<NotificationLevel>;
  drawerVisible: Ref<boolean>;
  filters: NotificationFilters;
  sourceOptions: ComputedRef<string[]>;
  fundOptions: ComputedRef<string[]>;
  accountOptions: ComputedRef<string[]>;
  levelOptions: ComputedRef<NotificationLevel[]>;
  oldestHistoryTs: ComputedRef<number | null>;
  historyLoading: Ref<boolean>;
  historyHasMore: Ref<boolean>;
  realtimeVersion: Ref<number>;
  open: () => void;
  close: () => void;
  toggle: () => void;
  push: (rawMsg: RawNotification) => void;
  mute: (key: string) => void;
  unmute: (key: string) => void;
  isMuted: (key: string) => boolean;
  clearAll: () => void;
  clearRead: () => void;
  setUserScope: (username: string) => void;
  beginHistoryLoad: () => number | null;
  finishHistoryLoad: (hasMore: boolean, oldestTs?: unknown) => void;
  cancelHistoryLoad: () => void;
}

interface NormalizedNotification {
  id: string;
  state?: "firing" | "resolved";
  source: string;
  level: NotificationLevel;
  category: string;
  title: string;
  body: string;
  scope: NotificationScope;
  dedupKey?: string;
  ts: number;
  ttl?: number;
  actions: NotificationAction[];
  raw: RawNotification;
  seq: number;
  delivery: "history" | "realtime";
}

interface GroupAccumulator {
  notifications: NormalizedNotification[];
  highestLevel: NotificationLevel;
  latest: NormalizedNotification;
}

interface ReadState {
  latestTs: number;
  count: number;
}

const LEVEL_ORDER: Record<NotificationLevel, number> = {
  info: 0,
  warning: 1,
  error: 2,
  critical: 3,
};

const LEVEL_OPTIONS: NotificationLevel[] = ["info", "warning", "error", "critical"];

const displayPolicy = useNotificationDisplayPolicy();

const notifications = ref<NormalizedNotification[]>([]);
const drawerVisible = ref(false);
const nowSec = ref(currentUnixSec());
const mutedKeys = ref<Set<string>>(new Set());
const readStates = ref<Map<string, ReadState>>(new Map());
const filters = reactive<NotificationFilters>({});
const seenIds = new Set<string>();
const historyLoading = ref(false);
const historyHasMore = ref(true);
const realtimeVersion = ref(0);
const historyCursorTs = ref<number | null>(null);

let tickerId: number | null = null;
let sequence = 0;
let autoOpenedOnce = false;
let storageNamespace = "";

const STORAGE_PREFIX = "cookpit:notification-center:v1";

function storageKey(): string {
  return storageNamespace ? `${STORAGE_PREFIX}:${storageNamespace}` : "";
}

function persistUserState(): void {
  const key = storageKey();
  if (!key || typeof localStorage === "undefined") return;
  localStorage.setItem(
    key,
    JSON.stringify({
      mutedKeys: Array.from(mutedKeys.value),
      readStates: Array.from(readStates.value.entries()),
    }),
  );
}

function loadUserState(): void {
  const key = storageKey();
  if (!key || typeof localStorage === "undefined") return;
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "{}");
    mutedKeys.value = new Set(
      Array.isArray(raw.mutedKeys) ? raw.mutedKeys.map((item: unknown) => toText(item)) : [],
    );
    readStates.value = new Map(
      Array.isArray(raw.readStates)
        ? raw.readStates.filter(
            (item: unknown) => Array.isArray(item) && item.length === 2 && isRecord(item[1]),
          )
        : [],
    );
  } catch (error) {
    logger.warn("[NotificationBus] 本地状态恢复失败:", error);
  }
}

function setUserScope(username: string): void {
  const normalized = username.trim();
  if (storageNamespace === normalized) return;
  storageNamespace = normalized;
  notifications.value = [];
  mutedKeys.value = new Set();
  readStates.value = new Map();
  seenIds.clear();
  historyLoading.value = false;
  historyHasMore.value = true;
  realtimeVersion.value = 0;
  historyCursorTs.value = null;
  autoOpenedOnce = false;
  drawerVisible.value = false;
  displayPolicy.stop(true);
  loadUserState();
}

function currentUnixSec(): number {
  return Math.floor(Date.now() / 1000);
}

function startTicker(): void {
  if (tickerId !== null || typeof window === "undefined") return;
  tickerId = window.setInterval(() => {
    nowSec.value = currentUnixSec();
  }, 1000);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toText(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

function toNullableText(value: unknown): string | null {
  const text = toText(value).trim();
  return text ? text : null;
}

function normalizeLevel(value: unknown): NotificationLevel {
  if (value === "info" || value === "warning" || value === "error" || value === "critical") {
    return value;
  }
  return "warning";
}

function normalizeScope(value: unknown): NotificationScope {
  if (!isRecord(value)) {
    return { fund_id: null, account_id: null };
  }
  return {
    fund_id: toNullableText(value.fund_id),
    account_id: toNullableText(value.account_id),
  };
}

function normalizeActions(value: unknown): NotificationAction[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<NotificationAction[]>((result, action) => {
    if (!isRecord(action)) return result;
    const label = toText(action.label).trim();
    const url = toText(action.url).trim();
    if (label && url) {
      result.push({ label, url });
    }
    return result;
  }, []);
}

function normalizeUnixSec(value: unknown, fallback = currentUnixSec()): number {
  const numericValue =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(numericValue) || numericValue <= 0) return fallback;
  return Math.floor(numericValue);
}

function normalizeTtl(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const numericValue =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(numericValue) || numericValue < 0) return undefined;
  return Math.floor(numericValue);
}

function createNotificationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getNotificationKey(notification: NormalizedNotification): string {
  return notification.dedupKey || `${notification.category}|${notification.title}`;
}

function normalizeNotification(rawMsg: RawNotification): NormalizedNotification | null {
  if (!isRecord(rawMsg)) {
    logger.warn("[NotificationBus] 忽略非对象通知:", rawMsg);
    return null;
  }

  const table = toText(rawMsg.table);
  if (table === "alert") {
    return {
      id: createNotificationId(),
      source: "legacy",
      level: normalizeLevel(rawMsg.type),
      category: "legacy_alert",
      title: toText(rawMsg.title, "旧版告警").trim() || "旧版告警",
      body: toText(rawMsg.msg).trim(),
      scope: { fund_id: null, account_id: null },
      ts: currentUnixSec(),
      actions: [],
      raw: rawMsg,
      seq: ++sequence,
      delivery: "realtime",
    };
  }

  if (table !== "notification") {
    logger.warn("[NotificationBus] 忽略未知通知表:", table || rawMsg);
    return null;
  }

  const id = toText(rawMsg.id).trim() || createNotificationId();
  const category = toText(rawMsg.category, "general").trim() || "general";
  const title = toText(rawMsg.title, "通知").trim() || "通知";

  return {
    id,
    state:
      rawMsg.state === "resolved" ? "resolved" : rawMsg.state === "firing" ? "firing" : undefined,
    source: toText(rawMsg.source, "unknown").trim() || "unknown",
    level: rawMsg.state === "resolved" ? "info" : normalizeLevel(rawMsg.level),
    category,
    title,
    body: toText(rawMsg.body).trim(),
    scope: normalizeScope(rawMsg.scope),
    dedupKey: toNullableText(rawMsg.dedup_key) ?? undefined,
    ts: normalizeUnixSec(rawMsg.ts),
    ttl: normalizeTtl(rawMsg.ttl),
    actions: normalizeActions(rawMsg.actions),
    raw: rawMsg,
    seq: ++sequence,
    delivery: rawMsg.delivery === "history" ? "history" : "realtime",
  };
}

function isHigherLevel(candidate: NotificationLevel, current: NotificationLevel): boolean {
  return LEVEL_ORDER[candidate] > LEVEL_ORDER[current];
}

function isLatestNotification(
  candidate: NormalizedNotification,
  current: NormalizedNotification,
): boolean {
  if (candidate.ts !== current.ts) return candidate.ts > current.ts;
  return candidate.seq > current.seq;
}

function isExpired(notification: NormalizedNotification): boolean {
  if (notification.ttl === undefined) return false;
  return notification.ts + notification.ttl <= nowSec.value;
}

function isGroupRead(key: string, latestTs: number, count: number): boolean {
  const readState = readStates.value.get(key);
  if (!readState) return false;
  if (readState.latestTs !== latestTs) return readState.latestTs > latestTs;
  return readState.count >= count;
}

function markAllRead(): void {
  if (items.value.length === 0) return;
  const nextReadStates = new Map(readStates.value);
  for (const group of items.value) {
    nextReadStates.set(group.key, {
      latestTs: group.latestTs,
      count: group.count,
    });
  }
  readStates.value = nextReadStates;
}

function notifySevere(notification: NormalizedNotification): void {
  if (!displayPolicy.admits(notification.category) || notification.state === "resolved") return;
  if (notification.delivery === "history") return;
  if (notification.level !== "error" && notification.level !== "critical") return;

  ElMessage({
    type: "error",
    message: notification.body ? `${notification.title}: ${notification.body}` : notification.title,
    duration: notification.level === "critical" ? 0 : 3000,
    showClose: true,
  });

  if (!autoOpenedOnce) {
    drawerVisible.value = true;
    autoOpenedOnce = true;
  }
}

function isMutedKey(key: string | null | undefined): boolean {
  return Boolean(key && mutedKeys.value.has(key));
}

const items = computed<NotificationGroup[]>(() => {
  const grouped = new Map<string, GroupAccumulator>();

  for (const notification of notifications.value) {
    const key = getNotificationKey(notification);
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        notifications: [notification],
        highestLevel: notification.level,
        latest: notification,
      });
      continue;
    }

    existing.notifications.push(notification);
    if (isHigherLevel(notification.level, existing.highestLevel)) {
      existing.highestLevel = notification.level;
    }
    if (isLatestNotification(notification, existing.latest)) {
      existing.latest = notification;
    }
  }

  return Array.from(grouped.entries())
    .map(([key, group]) => ({
      key,
      level: group.latest.state ? group.latest.level : group.highestLevel,
      state: group.latest.state,
      category: group.latest.category,
      source: group.latest.source,
      title: group.latest.title,
      body: group.latest.body,
      count: group.notifications.length,
      latestTs: group.latest.ts,
      scope: group.latest.scope,
      actions: group.latest.actions,
      raw: group.notifications.map((notification) => notification.raw),
      ttl: group.latest.ttl,
      expired: isExpired(group.latest),
      read: isGroupRead(key, group.latest.ts, group.notifications.length),
    }))
    .sort((left, right) => {
      if (left.latestTs !== right.latestTs) return right.latestTs - left.latestTs;
      if (LEVEL_ORDER[left.level] !== LEVEL_ORDER[right.level]) {
        return LEVEL_ORDER[right.level] - LEVEL_ORDER[left.level];
      }
      return left.key.localeCompare(right.key);
    });
});

// 列表、角标、等级与严重弹窗使用相同的页面准入。
const visibleItems = computed<NotificationGroup[]>(() =>
  items.value.filter((group) => displayPolicy.admits(group.category)),
);

const filteredItems = computed<NotificationGroup[]>(() =>
  visibleItems.value.filter((group) => {
    if (filters.source && group.source !== filters.source) return false;
    if (filters.level && group.level !== filters.level) return false;
    if (filters.fundId && group.scope.fund_id !== filters.fundId) return false;
    if (filters.accountId && group.scope.account_id !== filters.accountId) return false;
    return true;
  }),
);

const unreadCount = computed(
  () => visibleItems.value.filter((group) => !group.expired && !group.read).length,
);

const highestLevel = computed<NotificationLevel>(() => {
  return visibleItems.value.reduce<NotificationLevel>((highest, group) => {
    if (group.expired) return highest;
    return isHigherLevel(group.level, highest) ? group.level : highest;
  }, "info");
});

function distinctNotificationValues(
  selector: (notification: NormalizedNotification) => string | null,
): string[] {
  const values = new Set<string>();
  for (const notification of notifications.value) {
    if (!displayPolicy.admits(notification.category)) continue;
    const value = selector(notification);
    if (value) values.add(value);
  }
  return Array.from(values).sort((left, right) => left.localeCompare(right));
}

const sourceOptions = computed(() =>
  distinctNotificationValues((notification) => notification.source || null),
);
const fundOptions = computed(() =>
  distinctNotificationValues((notification) => notification.scope.fund_id),
);
const accountOptions = computed(() =>
  distinctNotificationValues((notification) => notification.scope.account_id),
);
const levelOptions = computed<NotificationLevel[]>(() => [...LEVEL_OPTIONS]);
const oldestHistoryTs = computed<number | null>(() => historyCursorTs.value);

function trackHistoryCursor(value: unknown): void {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return;
  const normalized = Math.floor(timestamp);
  historyCursorTs.value =
    historyCursorTs.value === null ? normalized : Math.min(historyCursorTs.value, normalized);
}

function beginHistoryLoad(): number | null {
  if (historyLoading.value || !historyHasMore.value || oldestHistoryTs.value === null) {
    return null;
  }
  historyLoading.value = true;
  return oldestHistoryTs.value;
}

function finishHistoryLoad(hasMore: boolean, oldestTs?: unknown): void {
  trackHistoryCursor(oldestTs);
  historyLoading.value = false;
  historyHasMore.value = hasMore;
}

function cancelHistoryLoad(): void {
  historyLoading.value = false;
}

function open(): void {
  drawerVisible.value = true;
}

function close(): void {
  drawerVisible.value = false;
}

function toggle(): void {
  drawerVisible.value = !drawerVisible.value;
}

function push(rawMsg: RawNotification): void {
  try {
    if (isRecord(rawMsg) && rawMsg.table === "notification" && rawMsg.delivery === "history") {
      trackHistoryCursor(rawMsg.ts);
    }
    const notification = normalizeNotification(rawMsg);
    if (!notification) return;
    if (seenIds.has(notification.id)) return;
    seenIds.add(notification.id);

    const key = getNotificationKey(notification);
    if (isMutedKey(key) || isMutedKey(notification.scope.account_id)) return;

    notifications.value = [...notifications.value, notification];
    if (notification.delivery === "realtime") realtimeVersion.value += 1;
    notifySevere(notification);

    if (drawerVisible.value) {
      markAllRead();
      persistUserState();
    }
  } catch (error) {
    logger.error("[NotificationBus] push 失败:", error);
  }
}

function mute(key: string): void {
  const mutedKey = key.trim();
  if (!mutedKey) return;

  const nextMutedKeys = new Set(mutedKeys.value);
  nextMutedKeys.add(mutedKey);
  mutedKeys.value = nextMutedKeys;

  const removedGroupKeys = new Set<string>();
  notifications.value = notifications.value.filter((notification) => {
    const groupKey = getNotificationKey(notification);
    const shouldRemove = groupKey === mutedKey || notification.scope.account_id === mutedKey;
    if (shouldRemove) removedGroupKeys.add(groupKey);
    return !shouldRemove;
  });

  if (removedGroupKeys.size > 0) {
    const nextReadStates = new Map(readStates.value);
    for (const groupKey of removedGroupKeys) {
      nextReadStates.delete(groupKey);
    }
    readStates.value = nextReadStates;
  }
  persistUserState();
}

function unmute(key: string): void {
  const mutedKey = key.trim();
  if (!mutedKey || !mutedKeys.value.has(mutedKey)) return;
  const nextMutedKeys = new Set(mutedKeys.value);
  nextMutedKeys.delete(mutedKey);
  mutedKeys.value = nextMutedKeys;
  persistUserState();
}

function isMuted(key: string): boolean {
  return mutedKeys.value.has(key);
}

function clearAll(): void {
  notifications.value = [];
  readStates.value = new Map();
  historyLoading.value = false;
  historyHasMore.value = false;
  historyCursorTs.value = null;
  persistUserState();
}

function clearRead(): void {
  const readGroupKeys = new Set(
    items.value.filter((group) => group.read).map((group) => group.key),
  );
  if (readGroupKeys.size === 0) return;

  notifications.value = notifications.value.filter(
    (notification) => !readGroupKeys.has(getNotificationKey(notification)),
  );

  const nextReadStates = new Map(readStates.value);
  for (const key of readGroupKeys) {
    nextReadStates.delete(key);
  }
  readStates.value = nextReadStates;
  persistUserState();
}

watch(drawerVisible, (visible) => {
  if (visible) {
    markAllRead();
    persistUserState();
  }
});

startTicker();

export function useNotificationBus(): UseNotificationBusReturn {
  return {
    items,
    visibleItems,
    filteredItems,
    unreadCount,
    highestLevel,
    drawerVisible,
    filters,
    sourceOptions,
    fundOptions,
    accountOptions,
    levelOptions,
    oldestHistoryTs,
    historyLoading,
    historyHasMore,
    realtimeVersion,
    open,
    close,
    toggle,
    push,
    mute,
    unmute,
    isMuted,
    clearAll,
    clearRead,
    setUserScope,
    beginHistoryLoad,
    finishHistoryLoad,
    cancelHistoryLoad,
  };
}
